import { useMutation } from '@tanstack/react-query';
/** 复制项目可选择不同投放产品与渠道，沿用书籍和章节公共结果。 */
import { useState } from 'react';
import {
  type ProjectDetail,
  type ProjectMutation,
  useLandingMediaSources,
  useLandingProductsPage,
  useLandingProjectBookEligibility,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';
export function LandingProjectCopyDialog({
  project,
  onClose,
  onSaved,
}: { project: ProjectDetail; onClose: () => void; onSaved: (result: ProjectMutation) => void }) {
  const [name, setName] = useState(`${project.name} 副本`);
  const [productId, setProductId] = useState(project.product.id);
  const [channel, setChannel] = useState(project.media_source);
  const products = useLandingProductsPage({ page: 1, pageSize: 200 });
  const channels = useLandingMediaSources();
  const eligibility = useLandingProjectBookEligibility(productId, project.novel);
  const mutation = useMutation({
    mutationFn: () => {
      if (!project.can_generate || !eligibility.data)
        throw new Error(
          project.generation_blocked_reason ?? '这本书在目标产品不可投放，请更换产品',
        );
      return landingProjectsApi.copy(project.id, {
        name,
        product_id: productId,
        media_source: channel,
      });
    },
    onSuccess: onSaved,
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  return (
    <Dialog
      title="复制投放项目"
      subtitle="复用同书同语言章节结果；新项目的 HTML 需独立生成"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={
              !name.trim() ||
              !productId ||
              !channel ||
              mutation.isPending ||
              !project.can_generate ||
              eligibility.data !== true
            }
            onClick={() => mutation.mutate()}
          >
            复制项目
          </button>
        </>
      }
    >
      <div style={{ padding: '0 18px' }}>
        {!project.can_generate && (
          <p role="alert">{project.generation_blocked_reason ?? '当前书籍不能投放'}</p>
        )}
        {eligibility.isError && <p role="alert">投放资格校验失败：{eligibility.error.message}</p>}
        {eligibility.isLoading && <p className="muted">校验目标产品的书籍状态…</p>}
        {eligibility.data === false && <p role="alert">这本书在目标产品不可投放，请更换产品</p>}
        <FormRow label="项目名称" required>
          <input
            className="input"
            aria-label="副本项目名称"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={128}
          />
        </FormRow>
        <FormRow label="投放产品" required>
          <Select value={productId} onChange={(event) => setProductId(event.target.value)}>
            {products.data.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="投放渠道" required>
          <Select value={channel} onChange={(event) => setChannel(event.target.value)}>
            {channels.data.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </Select>
        </FormRow>
      </div>
    </Dialog>
  );
}
